import React from "react";
import { useSelector } from "react-redux";

import { Star } from "~/components/star";
import { type NodeId, Permission } from "~/models/dto";
import { prop } from "~/tools/prop";

import { selectPlayground } from "../../../app/state/config/ConfigSelectors";
import { usePermissions } from "../../../common/use-permissions";
import { useUserProfile } from "../../../common/use-user-profile";
import { VIEWS_OVERVIEW_PATH } from "../../../routes/Routes";
import { LinkTo } from "../link";
import { Tooltip } from "../tooltip/Tooltip";
import { TrialTooltip } from "../tooltip/TrialTooltip";

export interface ViewStarProps {
	viewId: NodeId;
	className?: string;
}

export const ViewStar: React.FC<ViewStarProps> = ({ viewId, ...props }) => {
	const permissions = usePermissions();
	const [starredViews, updateUserProfile] = useUserProfile({
		select: prop("starredViews"),
	});

	const isStarred = starredViews?.has(viewId);
	const isPlayground = useSelector(selectPlayground);

	if (isStarred && permissions?.hasSystemPermission(Permission.DeleteFavoriteViews)) {
		return (
			<ViewStarTooltip isPlayground={isPlayground} prefix="Remove from ">
				<Star
					{...props}
					onStarredChange={() => {
						if (!isPlayground) {
							updateUserProfile((userProfile) => ({
								...userProfile,
								starredViews: userProfile.starredViews.delete(viewId),
							}));
						}
					}}
					starred
				/>
			</ViewStarTooltip>
		);
	}

	if (!isStarred && permissions?.hasSystemPermission(Permission.CreateFavoriteViews)) {
		return (
			<ViewStarTooltip isPlayground={isPlayground} prefix="Add to ">
				<Star
					{...props}
					disabled={isStarred == null}
					onStarredChange={() => {
						if (!isPlayground) {
							updateUserProfile((userProfile) => ({
								...userProfile,
								starredViews: userProfile.starredViews.add(viewId),
							}));
						}
					}}
					starred={false}
				/>
			</ViewStarTooltip>
		);
	}

	return null;
};

const ViewStarTooltip: React.FC<
	React.PropsWithChildren<{
		prefix: string;
		isPlayground: boolean;
	}>
> = ({ prefix, children, isPlayground }) => {
	if (isPlayground) {
		return <TrialTooltip>{children}</TrialTooltip>;
	}

	return (
		<Tooltip
			title={
				<span className="select-none">
					{prefix}
					<LinkTo
						className="text-white underline visited:text-white hover:text-white hover:no-underline"
						onClick={(event) => event.stopPropagation()}
						search={({ timeRange, timestamp, detachedFilters }) => ({
							timeRange,
							timestamp,
							detachedFilters,
							viewFilter: "starred",
						})}
						to={VIEWS_OVERVIEW_PATH}
					>
						starred views
					</LinkTo>
				</span>
			}
		>
			{children}
		</Tooltip>
	);
};
